<template>
  <div class="chat-demo">
    <!-- Chat Interface -->
    <div class="bg-white rounded-lg border border-gray-200">
      <!-- Chat Header -->
      <div class="bg-blue-600 text-white p-3 rounded-t-lg flex items-center justify-between">
        <div class="flex items-center gap-2">
          <IconifyIcon icon="heroicons:chat-bubble-left-right-20-solid" class="w-5 h-5" />
          <span class="font-medium">Demo Chat</span> <span class="ml-1 text-xs font-normal opacity-80">(simulated, no real connection)</span>
        </div>
        <div class="flex items-center gap-1">
          <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span class="text-xs">Connected</span>
        </div>
      </div>

      <!-- Messages -->
      <div class="h-48 overflow-y-auto p-4 space-y-3">
        <div
          v-for="message in messages"
          :key="message.id"
          :class="[
            'flex',
            message.sender === 'me' ? 'justify-end' : 'justify-start'
          ]"
        >
          <div
            :class="[
              'max-w-xs px-4 py-2 rounded-lg',
              message.sender === 'me'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-900'
            ]"
          >
            <p class="text-sm">{{ message.text }}</p>
            <p class="text-xs opacity-70 mt-1">
              {{ message.time }}
            </p>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-if="isTyping" class="flex justify-start">
          <div class="bg-gray-100 px-4 py-2 rounded-lg">
            <div class="flex items-center gap-1">
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.1s;"></div>
              <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s;"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input -->
      <div class="border-t border-gray-200 p-3">
        <div class="flex gap-2">
          <input
            v-model="inputMessage"
            @keyup.enter="sendMessage"
            type="text"
            placeholder="Type a message..."
            class="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <button
            @click="sendMessage"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <IconifyIcon icon="heroicons:paper-airplane-20-solid" class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import IconifyIcon from '../../IconifyIcon.vue'

interface Message {
  id: number
  sender: 'me' | 'other'
  text: string
  time: string
}

// Reactive state
const inputMessage = ref('')
const isTyping = ref(false)
const messages = ref<Message[]>([
  {
    id: 1,
    sender: 'other',
    text: 'Welcome to WebRTC QR Chat! 🎉',
    time: '12:00 PM'
  },
  {
    id: 2,
    sender: 'other',
    text: 'This is a demo of the real-time chat feature.',
    time: '12:00 PM'
  }
])

// Methods
const sendMessage = () => {
  if (!inputMessage.value.trim()) return

  // Add user message
  messages.value.push({
    id: messages.value.length + 1,
    sender: 'me',
    text: inputMessage.value,
    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  })

  inputMessage.value = ''

  // Simulate typing and response
  isTyping.value = true
  setTimeout(() => {
    isTyping.value = false
    messages.value.push({
      id: messages.value.length + 1,
      sender: 'other',
      text: 'Message received! In a real connection, this would appear instantly on the other device.',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    })
  }, 1500)
}

// Lifecycle
onMounted(() => {
  // Simulate a welcome message after a delay
  setTimeout(() => {
    messages.value.push({
      id: 3,
      sender: 'other',
      text: 'Try sending a message above to see how it works!',
      time: '12:01 PM'
    })
  }, 2000)
})
</script>

<style scoped>
.chat-demo {
  /* Component-specific styles */
}
</style>